import { useState, useEffect } from "react";
import { Box, Grid, IconButton, Typography, Tooltip } from "@mui/material";
import { Add as AddIcon, Edit as EditIcon, Cancel as CancelIcon } from "@mui/icons-material";
import { 
  IDefaultRouteCostsResponse, 
  IVehicleTypeResponse, 
  IDefaultRouteCostWithObject, 
  IDefaultRouteCostsData
} from "../types";
import { toast } from "react-toastify";
import { CostLineCard } from "./CostLineCard";
import NewButton from "components/NewButton";
import { validateForm } from '../utils/validation';

interface EditDefaultCostFormProps {
  form: IDefaultRouteCostsResponse;
  onSave: (form: IDefaultRouteCostsData) => void;
  loading: boolean;
  vehicleTypes: IVehicleTypeResponse[];
}

const EditDefaultCostForm = ({
  form: initialForm,
  onSave,
  loading,
  vehicleTypes,
}: EditDefaultCostFormProps) => {
  const [form, setForm] = useState<IDefaultRouteCostsData>({
    transshipPointId: initialForm.transshipPoint.id,
    costs: initialForm.costs.map(item => ({
      vehicleTypeId: item.vehicleType.id,
      initialCapacity: item.initialCapacity,
      finalCapacity: item.finalCapacity,
      cost: item.cost,
      leaderCommission: item.leaderCommission,
      additionalDelivery: item.additionalDelivery || 0,
      unloadingCost: item.unloadingCost ?? null,
    }))
  });
  const [isEditing, setIsEditing] = useState(false);
  const [originalForm, setOriginalForm] = useState<IDefaultRouteCostsData>({
    transshipPointId: initialForm.transshipPoint.id,
    costs: initialForm.costs.map(item => ({
      vehicleTypeId: item.vehicleType.id,
      initialCapacity: item.initialCapacity,
      finalCapacity: item.finalCapacity,
      cost: item.cost,
      leaderCommission: item.leaderCommission,
      additionalDelivery: item.additionalDelivery || 0,
      unloadingCost: item.unloadingCost ?? null,
    }))
  });
  const [errors, setErrors] = useState<{ costs: any[] }>({ costs: [] });
  
  // Obtém os tipos de veículos disponíveis para um formulário específico
  const getAvailableVehicleTypes = (currentItemIndex: number) => {
    const usedTypes = new Set(
      form.costs
        .filter((_, index) => index !== currentItemIndex)
        .map(item => item.vehicleTypeId)
    );
    return vehicleTypes.filter(type => !usedTypes.has(Number(type.id)));
  };

  // Adicionar uma nova linha de item
  const handleAddItem = () => {
    const availableTypes = getAvailableVehicleTypes(-1);
    if (availableTypes.length === 0) {
      toast.error("Não há mais tipos de veículo disponíveis para adicionar");
      return;
    }

    const lastItem = form.costs[form.costs.length - 1];
    const newItem = {
      vehicleTypeId: Number(availableTypes[0].id),
      initialCapacity: lastItem ? lastItem.finalCapacity + 1 : 0,
      finalCapacity: 0,
      cost: 0,
      leaderCommission: 0,
      additionalDelivery: 0,
      unloadingCost: null,
    };

    setForm({
      ...form,
      costs: [...form.costs, newItem]
    });
  };

  // Remover um item específico
  const handleRemoveItem = (itemIndex: number) => {
    setForm({
      ...form,
      costs: form.costs.filter((_, index) => index !== itemIndex)
    });
  };

  // Atualizar valores de um item
  const handleItemChange = (
    itemIndex: number,
    field: keyof IDefaultRouteCostWithObject,
    value: any
  ) => {
    const newCosts = [...form.costs];
    
    newCosts[itemIndex] = {
      ...newCosts[itemIndex],
      [field]: typeof value === 'number' ? value : Number(value)
    };
    
    setForm({
      ...form,
      costs: newCosts
    });
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setForm(originalForm);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const { isValid, errors: validationErrors } = validateForm(form);
    setErrors(validationErrors);
    if (!isValid) return;
    
    try {
      await onSave(form);
      setIsEditing(false);
      setOriginalForm(form);
    } catch (error) {}
  };

  // Atualiza o estado quando as props mudarem
  useEffect(() => {
    const newForm = {
      transshipPointId: initialForm.transshipPoint.id,
      costs: initialForm.costs.map(item => ({
        vehicleTypeId: item.vehicleType.id,
        initialCapacity: item.initialCapacity,
        finalCapacity: item.finalCapacity,
        cost: item.cost,
        leaderCommission: item.leaderCommission,
        additionalDelivery: item.additionalDelivery || 0,
        unloadingCost: item.unloadingCost ?? null,
      }))
    };
    setForm(newForm);
    setOriginalForm(newForm);
    setErrors({ costs: [] }); // Limpa os erros ao abrir novo formulário
  }, [initialForm]);

  return (
    <Box>
      {/* Botão de editar/cancelar acima dos cards */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        {!isEditing ? (
          <Tooltip title="Editar">
            <IconButton 
              onClick={handleEdit} 
              disabled={loading} 
              size="medium"
              sx={{
                background: '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': {
                  background: '#f5f5f5',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Descartar alterações">  
            <IconButton 
              onClick={handleCancel} 
              disabled={loading} 
              size="medium"
              sx={{
                background: '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': {
                  background: '#f5f5f5',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }
              }}
            >
              <CancelIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box px={0} py={2} style={{ background: '#fff', borderRadius: 8 }}>
        {/* Linhas de custo como cards */}
      {(isEditing ? form.costs : form.costs.sort((a, b) => a.initialCapacity - b.initialCapacity))
        .map((item, itemIndex) => (
          <CostLineCard
            key={itemIndex}
            item={item}
            itemIndex={itemIndex}
            errors={errors.costs[itemIndex]}
            loading={loading}
            isEditing={isEditing}
            onChange={(itemIndex, field, value) => handleItemChange(itemIndex, field as any, value)}
            onRemove={handleRemoveItem}
            getAvailableVehicleTypes={getAvailableVehicleTypes}
            disableRemove={form.costs.length <= 1}
          />
        ))}
      {isEditing && (
        <Box display="flex" justifyContent="flex-end" gap={[1, 3]} mt={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
          <NewButton
            variantType="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddItem}
            disabled={loading || getAvailableVehicleTypes(-1).length === 0}
          >
            Adicionar veículo
          </NewButton>
          <NewButton variantType="filled" onClick={handleSave} disabled={loading}>
            Salvar
          </NewButton>
        </Box>
      )}
      </Box>
    </Box>
  );
};

export default EditDefaultCostForm; 